import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FormDefinition,
  FormField,
  CreateFormRequest,
  FORM_CATEGORIES,
  FORM_FIELD_TYPES,
  FIELD_WIDTHS,
  FormFieldType,
  FieldWidth
} from '../../types/forms';

interface FormBuilderProps {
  form?: FormDefinition | null;
  onSave: (formData: CreateFormRequest) => Promise<void>;
  onCancel: () => void;
}

interface SortableFieldProps {
  field: FormField;
  index: number;
  updateField: (index: number, updates: Partial<FormField>) => void;
  removeField: (index: number) => void;
}

const SortableFieldEditor: React.FC<SortableFieldProps> = ({ field, index, updateField, removeField }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper 
      ref={setNodeRef}
      style={style}
      sx={{ 
        p: 0, 
        mb: 1.5,
        border: isDragging ? '2px dashed #1976d2' : '1px solid #e0e0e0'
      }}
    >
      {/* Separate draggable handle */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, pb: 1.5 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'grab',
            mr: 1,
            '&:active': { cursor: 'grabbing' }
          }}
        >
          <DragIcon sx={{ color: 'text.secondary' }} />
        </Box>
        <Typography component="div" variant="h6" sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 600 }}>
          Field {index + 1}: {field.label}
        </Typography>
        <Chip label={field.type} size="small" sx={{ mr: 1 }} />
        <IconButton size="small" color="error" onClick={() => removeField(index)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* Field configuration content */}
      <Box sx={{ px: 2, pb: 2 }}>

      <Stack spacing={2}>
        {/* Basic field configuration */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Field Label"
              value={field.label}
              onChange={(e) => updateField(index, { label: e.target.value })}
              size="small"
            />
          </Box>
          
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Field Type</InputLabel>
              <Select
                value={field.type}
                label="Field Type"
                onChange={(e) => updateField(index, { type: e.target.value as FormFieldType })}
              >
                {FORM_FIELD_TYPES.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Placeholder"
              value={field.placeholder || ''}
              onChange={(e) => updateField(index, { placeholder: e.target.value })}
              size="small"
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Width</InputLabel>
              <Select
                value={field.width || 'full'}
                label="Width"
                onChange={(e) => updateField(index, { width: e.target.value as FieldWidth })}
              >
                {FIELD_WIDTHS.map(width => (
                  <MenuItem key={width} value={width}>
                    {width.charAt(0).toUpperCase() + width.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Help Text"
          value={field.helpText || ''}
          onChange={(e) => updateField(index, { helpText: e.target.value })}
          size="small"
          multiline
          rows={2}
        />

        <FormControlLabel
          control={
            <Switch
              checked={field.required || false}
              onChange={(e) => updateField(index, { required: e.target.checked })}
            />
          }
          label="Required Field"
        />

        {/* Options for select/radio/checkbox fields */}
        {(['select', 'multiselect', 'radio', 'checkbox'].includes(field.type)) && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
            {(field.options || []).map((option, optionIndex) => (
              <Box key={optionIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label="Value"
                  value={option.value}
                  onChange={(e) => {
                    const newOptions = [...(field.options || [])];
                    newOptions[optionIndex] = { ...option, value: e.target.value };
                    updateField(index, { options: newOptions });
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Label"
                  value={option.label}
                  onChange={(e) => {
                    const newOptions = [...(field.options || [])];
                    newOptions[optionIndex] = { ...option, label: e.target.value };
                    updateField(index, { options: newOptions });
                  }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    const newOptions = (field.options || []).filter((_, i) => i !== optionIndex);
                    updateField(index, { options: newOptions });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const newOptions = [...(field.options || []), { value: '', label: '' }];
                updateField(index, { options: newOptions });
              }}
            >
              Add Option
            </Button>
          </Box>
        )}

        {/* Validation rules */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Validation Rules</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {(['text', 'textarea'].includes(field.type)) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Min Length"
                    type="number"
                    value={field.validation?.minLength || ''}
                    onChange={(e) => updateField(index, {
                      validation: {
                        ...field.validation,
                        minLength: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Max Length"
                    type="number"
                    value={field.validation?.maxLength || ''}
                    onChange={(e) => updateField(index, {
                      validation: {
                        ...field.validation,
                        maxLength: e.target.value ? parseInt(e.target.value) : undefined
                      }
                    })}
                    size="small"
                  />
                </Box>
              )}
              
              {(['number', 'currency'].includes(field.type)) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Minimum Value"
                    type="number"
                    value={field.validation?.min || ''}
                    onChange={(e) => updateField(index, {
                      validation: {
                        ...field.validation,
                        min: e.target.value ? parseFloat(e.target.value) : undefined
                      }
                    })}
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Maximum Value"
                    type="number"
                    value={field.validation?.max || ''}
                    onChange={(e) => updateField(index, {
                      validation: {
                        ...field.validation,
                        max: e.target.value ? parseFloat(e.target.value) : undefined
                      }
                    })}
                    size="small"
                  />
                </Box>
              )}
              
              <TextField
                fullWidth
                label="Pattern (Regex)"
                value={field.validation?.pattern || ''}
                onChange={(e) => updateField(index, {
                  validation: {
                    ...field.validation,
                    pattern: e.target.value
                  }
                })}
                size="small"
                helperText="Regular expression for validation"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
      </Box>
    </Paper>
  );
};

const FormBuilder: React.FC<FormBuilderProps> = ({ form, onSave, onCancel }) => {
  const [formData, setFormData] = useState<CreateFormRequest>({
    name: '',
    description: '',
    category: 'Other',
    fields: [],
    styling: {
      theme: 'default',
      layout: 'single-column'
    },
    notifications: {
      onSubmit: []
    }
  });

  const [saving, setSaving] = useState(false);

  // Configure sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (form) {
      setFormData({
        name: form.name,
        description: form.description || '',
        category: form.category,
        fields: form.fields,
        styling: form.styling,
        notifications: form.notifications
      });
    }
  }, [form]);

  const handleBasicInfoChange = (field: keyof CreateFormRequest, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStylingChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      styling: {
        ...prev.styling,
        [field]: value
      }
    }));
  };

  const addField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      order: formData.fields.length,
      width: 'full'
    };

    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => 
        i === index ? { ...field, ...updates } : field
      )
    }));
  };

  const removeField = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    const newFields = arrayMove(formData.fields, fromIndex, toIndex);
    
    // Update order
    const updatedFields = newFields.map((field, index) => ({
      ...field,
      order: index
    }));

    setFormData(prev => ({
      ...prev,
      fields: updatedFields
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formData.fields.findIndex(field => field.id === active.id);
      const newIndex = formData.fields.findIndex(field => field.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        moveField(oldIndex, newIndex);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(formData);
    } catch (error) {
      console.error('Error saving form:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onCancel} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          {form ? 'Edit Form' : 'Create Form'}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !formData.name}
          sx={{ borderRadius: 2 }}
        >
          {saving ? 'Saving...' : 'Save Form'}
        </Button>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start'
      }}>
        {/* Form Configuration */}
        <Box sx={{ 
          width: { xs: '100%', md: '33.333%' }, 
          flexShrink: 0 
        }}>
          <Paper sx={{ p: 2.5, height: 'fit-content', position: 'sticky', top: 20 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Form Configuration
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Form Name"
                value={formData.name}
                onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                required
              />

              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                multiline
                rows={3}
              />

              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleBasicInfoChange('category', e.target.value)}
                >
                  {FORM_CATEGORIES.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Styling
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Layout</InputLabel>
                <Select
                  value={formData.styling?.layout || 'single-column'}
                  label="Layout"
                  onChange={(e) => handleStylingChange('layout', e.target.value)}
                >
                  <MenuItem value="single-column">Single Column</MenuItem>
                  <MenuItem value="two-column">Two Column</MenuItem>
                  <MenuItem value="wizard">Wizard</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={formData.styling?.theme || 'default'}
                  label="Theme"
                  onChange={(e) => handleStylingChange('theme', e.target.value)}
                >
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                  <MenuItem value="spacious">Spacious</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Box>

        {/* Form Fields */}
        <Box sx={{ 
          width: { xs: '100%', md: '66.666%' }, 
          flexGrow: 1 
        }}>
          <Paper sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Form Fields ({formData.fields.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addField}
                size="small"
              >
                Add Field
              </Button>
            </Box>

            {formData.fields.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                color: 'text.secondary',
                border: '2px dashed #e0e0e0',
                borderRadius: 2
              }}>
                <Typography variant="body1">
                  No fields added yet. Click "Add Field" to get started.
                </Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={formData.fields.map(field => field.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box>
                    {formData.fields.map((field, index) => (
                      <SortableFieldEditor
                        key={field.id}
                        field={field}
                        index={index}
                        updateField={updateField}
                        removeField={removeField}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default FormBuilder;
